import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Popup from 'reactjs-popup';
import { SeatsioSeatingChart } from '@seatsio/seatsio-react';
import usePageColorScheme from '../../shared/hooks/usePageColorScheme';
import { Button, Checkbox, Modal, Textarea } from '@opendate-app/setlist';
import UpgradePaymentFields, {
  upgradeExpectedTotal, upgradePaymentMethod, upgradeReady, upgradeRefundAmount, useUpgradeQuote,
} from './UpgradePaymentFields';

const SeatReassignmentModal = ({ ticket, tickets, initialPicks, seating, paymentMethods, currency, submitting, onClose, onSubmit, onSubmitMany, onPreview, canRefund, onPreviewEmail }) => {
  const [picked, setPicked] = useState(null);
  const [pickedMany, setPickedMany] = useState(initialPicks || []);
  const [emailFan, setEmailFan] = useState(true);
  const [fanNote, setFanNote] = useState('');
  const [emailPreview, setEmailPreview] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [refundDifference, setRefundDifference] = useState(false);
  const colorScheme = usePageColorScheme();

  const pickedCategory = picked && picked.category && String(picked.category.key);
  const changesTicketType = Boolean(ticket && pickedCategory && pickedCategory !== ticket.seatingCategoryKey);
  const quote = useUpgradeQuote(Boolean(picked), picked && picked.id, onPreview);

  if ((!ticket && !tickets) || !seating) return null;

  if (tickets) {
    const fans = [...new Set(tickets.map((row) => row.fan))];
    const slots = tickets.map((_, index) => pickedMany[index] || null);
    const pickedCount = slots.filter(Boolean).length;
    const allPicked = pickedCount === tickets.length;
    const submitMany = (event) => {
      event.preventDefault();
      if (!allPicked || submitting) return;
      onSubmitMany(tickets.map((row, index) => ({ ticketId: row.id, label: slots[index] })), { emailFan, fanNote });
    };

    return (
      <Popup open onClose={onClose} modal closeOnDocumentClick className="popup-modal">
        <form className="op-modal" onSubmit={submitMany}>
          <h2 className="op-section-title">{`Pick ${tickets.length} ${tickets.length === 1 ? 'seat' : 'seats'} for ${fans.length === 1 ? fans[0] : `${fans.length} fans`}`}</h2>
          <p className="op-muted">Click open seats in order. Click a picked seat again to give it back.</p>
          <div className="op-seat-chart">
            <SeatsioSeatingChart
              workspaceKey={seating.publicKey}
              event={seating.eventKey}
              region="na"
              maxSelectedObjects={tickets.length}
              selectedObjects={initialPicks ? initialPicks.filter(Boolean) : undefined}
              colorScheme={colorScheme}
              availableCategories={[...new Set(tickets.map((row) => row.seatingCategoryKey))]}
              onObjectSelected={(object) => setPickedMany((current) => {
                const next = tickets.map((_, index) => current[index] || null);
                if (next.includes(object.label)) return current;
                const sameSection = tickets.findIndex((row, index) => !next[index] && String(row.seatingCategoryKey) === String(object.category && object.category.key));
                const slot = sameSection === -1 ? next.indexOf(null) : sameSection;
                if (slot === -1) return current;
                next[slot] = object.label;
                return next;
              })}
              onObjectDeselected={(object) => setPickedMany((current) => current.map((label) => (label === object.label ? null : label)))}
            />
          </div>
          <p>{`${pickedCount} of ${tickets.length} picked`}</p>
          {tickets.map((row, index) => (
            <p key={row.id} className="op-muted">{`${row.fan} · ${row.ticketLabel} · ${row.seat} → ${slots[index] || '—'}`}</p>
          ))}
          <p className="op-muted">Fans stay in their own section and keep what they paid. To move a fan to another section, use Change seat.</p>
          <div style={{ marginTop: 12 }}>
            <Checkbox label="Email fans their new seats" checked={emailFan} onChange={(event) => setEmailFan(event.target.checked)} />
            <p className="op-muted">Tickets and wallet passes update either way. Each order gets one email listing all its new seats.</p>
            {emailFan && <Textarea label="Note to fans (optional)" rows={2} value={fanNote} onChange={(event) => setFanNote(event.target.value)} />}
          </div>
          <div className="op-modal-actions">
            <Button variant="secondary" onClick={onClose} disabled={submitting}>Cancel</Button>
            <Button type="submit" variant="primary" disabled={!allPicked || submitting}>
              {submitting ? 'Moving…' : `Move ${tickets.length} ${tickets.length === 1 ? 'ticket' : 'tickets'}`}
            </Button>
          </div>
        </form>
      </Popup>
    );
  }

  const newTicketTypeName = changesTicketType && seating.ticketTypeNamesByCategory[pickedCategory];
  const ready = Boolean(picked) && upgradeReady(true, quote, paymentMethod);

  const submit = (event) => {
    event.preventDefault();
    if (!ready || submitting) return;
    onSubmit(picked.id, upgradePaymentMethod(quote, paymentMethod), upgradeExpectedTotal(quote, paymentMethod), upgradeRefundAmount(quote, refundDifference), { emailFan, fanNote });
  };

  const previewEmail = () => onPreviewEmail(picked.id, { fanNote }).then(setEmailPreview).catch(() => {});

  return (
    <Popup open onClose={onClose} modal closeOnDocumentClick className="popup-modal">
      <form className="op-modal" onSubmit={submit}>
        <h2 className="op-section-title">{ticket.seatAssignment ? 'Change seat' : 'Pick a seat'}</h2>
        <p className="op-muted">{ticket.seatAssignment ? `Current seat: ${ticket.seatAssignment}` : `Pick a seat for this ${ticket.productName || 'GA'} ticket.`}</p>

        <div className="op-seat-chart">
          <SeatsioSeatingChart
            workspaceKey={seating.publicKey}
            event={seating.eventKey}
            region="na"
            maxSelectedObjects={1}
            colorScheme={colorScheme}
            availableCategories={Object.keys(seating.ticketTypeNamesByCategory)}
            onObjectSelected={(object) => { setPicked(object); setPaymentMethod(null); setRefundDifference(false); }}
            onObjectDeselected={() => setPicked(null)}
          />
        </div>

        {picked && <p>Move to {picked.label}</p>}
        {changesTicketType && <p className="op-muted">Ticket type will change to {newTicketTypeName}.</p>}
        {picked && (
          <UpgradePaymentFields
            changesTicketType
            quote={quote}
            paidAmount={ticket.finalCost}
            currency={currency}
            paymentMethods={paymentMethods}
            paymentMethod={paymentMethod}
            onChange={setPaymentMethod}
            canRefund={canRefund}
            refundDifference={refundDifference}
            onRefundDifferenceChange={setRefundDifference}
          />
        )}

        {picked && (
          <div style={{ marginTop: 12 }}>
            <Checkbox label="Email fans their new seats" checked={emailFan} onChange={(event) => setEmailFan(event.target.checked)} />
            <p className="op-muted">Tickets and wallet passes update either way.</p>
            {emailFan && (
              <>
                <Textarea label="Note to fans (optional)" rows={2} value={fanNote} onChange={(event) => setFanNote(event.target.value)} />
                {onPreviewEmail && <Button variant="secondary" size="sm" style={{ marginTop: 8 }} onClick={previewEmail}>Preview email</Button>}
              </>
            )}
          </div>
        )}

        <div className="op-modal-actions">
          <Button variant="secondary" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button type="submit" variant="primary" disabled={!ready || submitting}>
            {ticket.seatAssignment ? (submitting ? 'Changing…' : 'Change seat') : (submitting ? 'Moving…' : 'Move to this seat')}
          </Button>
        </div>
      </form>
      <Modal
        open={!!emailPreview}
        onClose={() => setEmailPreview(null)}
        title="Email preview"
        description={emailPreview ? `Goes to the buyer, like the order confirmation. To: ${emailPreview.to}` : ''}
        footer={<Button variant="secondary" onClick={() => setEmailPreview(null)}>Close</Button>}
      >
        {emailPreview && (
          <>
            <p><strong>{emailPreview.subject}</strong></p>
            <iframe sandbox="" title="Email preview" srcDoc={emailPreview.html} style={{ width: '100%', height: 420, border: '1px solid #e5e7eb', borderRadius: 8 }} />
          </>
        )}
      </Modal>
    </Popup>
  );
};

SeatReassignmentModal.propTypes = {
  ticket: PropTypes.shape({
    id: PropTypes.number.isRequired,
    seatAssignment: PropTypes.string,
    productName: PropTypes.string,
    seatingCategoryKey: PropTypes.string,
    finalCost: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }),
  initialPicks: PropTypes.arrayOf(PropTypes.string),
  tickets: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    seat: PropTypes.string,
    fan: PropTypes.string,
    ticketLabel: PropTypes.string,
    seatingCategoryKey: PropTypes.string,
  })),
  seating: PropTypes.shape({
    publicKey: PropTypes.string,
    eventKey: PropTypes.string.isRequired,
    ticketTypeNamesByCategory: PropTypes.objectOf(PropTypes.string).isRequired,
  }),
  paymentMethods: PropTypes.arrayOf(PropTypes.string),
  currency: PropTypes.string,
  submitting: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func,
  onSubmitMany: PropTypes.func,
  onPreview: PropTypes.func,
  canRefund: PropTypes.bool,
  onPreviewEmail: PropTypes.func,
};

SeatReassignmentModal.defaultProps = {
  currency: null,
  paymentMethods: [],
  ticket: null,
  tickets: null,
  initialPicks: null,
  onSubmit: null,
  onSubmitMany: null,
  seating: null,
  submitting: false,
  onPreview: null,
  canRefund: false,
  onPreviewEmail: null,
};

export default SeatReassignmentModal;
